/**
 * 가격 코리도 데이터 액세스 (pricing-corridor, 0005). Import from "@/lib/db/pricing".
 * backend-less thin repo — getSupabase() 경유, snake_case 행 1:1. env-safe.
 */

import { getSupabase, isSupabaseConfigured } from "@/lib/supabase";
import type {
  FxRate,
  PricePolicy,
  PricePolicyWithTiers,
  PriceTierRow,
  StandardCost,
} from "@/types";

export const priceRepo = {
  /** 제품×국가의 active 정책 + 티어 (최신 effective_from). 없으면 null. */
  async policyFor(
    productCode: string,
    countryCode: string,
  ): Promise<PricePolicyWithTiers | null> {
    if (!isSupabaseConfigured()) return null;
    const { data: pol, error } = await getSupabase()
      .from("price_policy")
      .select("*")
      .eq("product_code", productCode)
      .eq("country_code", countryCode)
      .eq("status", "active")
      .order("effective_from", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`[db] price_policy.policyFor: ${error.message}`);
    if (!pol) return null;
    const policy = pol as PricePolicy;
    const { data: tiers, error: te } = await getSupabase()
      .from("price_tier")
      .select("*")
      .eq("policy_id", policy.id)
      .order("sort_order");
    if (te) throw new Error(`[db] price_tier: ${te.message}`);
    return { ...policy, tiers: (tiers ?? []) as PriceTierRow[] };
  },

  /** 제품의 모든 active 정책 (국가 비교용). */
  async listActive(productCode: string): Promise<PricePolicy[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await getSupabase()
      .from("price_policy")
      .select("*")
      .eq("product_code", productCode)
      .eq("status", "active")
      .order("country_code");
    if (error) throw new Error(`[db] price_policy.listActive: ${error.message}`);
    return (data ?? []) as PricePolicy[];
  },
};

export const fxRepo = {
  /** 최신 환율 1건 (없으면 null). */
  async latest(pair = "USDKRW"): Promise<FxRate | null> {
    if (!isSupabaseConfigured()) return null;
    const { data, error } = await getSupabase()
      .from("fx_rate")
      .select("*")
      .eq("pair", pair)
      .order("fetched_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`[db] fx_rate.latest: ${error.message}`);
    return (data as FxRate) ?? null;
  },

  /** 수동 환율 입력 (이력 append). */
  async putManual(rate: number, pair = "USDKRW"): Promise<void> {
    if (!isSupabaseConfigured()) return;
    const { error } = await getSupabase().from("fx_rate").insert({
      pair,
      rate,
      source: "manual",
      fetched_at: new Date().toISOString(),
    });
    if (error) throw new Error(`[db] fx_rate.putManual: ${error.message}`);
  },
};

export const costRepo = {
  /** 최신 표준원가 (순/총). 없으면 null. */
  async latest(productCode: string): Promise<StandardCost | null> {
    if (!isSupabaseConfigured()) return null;
    const { data, error } = await getSupabase()
      .from("standard_cost")
      .select("*")
      .eq("product_code", productCode)
      .order("effective_from", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw new Error(`[db] standard_cost.latest: ${error.message}`);
    return (data as StandardCost) ?? null;
  },
};
